import { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import './Pages.css';

const RATE_LIMIT_KEY = 'gosplitpdf-contact-submissions';
const RATE_LIMIT_MAX = 3;
const RATE_LIMIT_WINDOW = 3600000; // 1 hour in milliseconds
const MIN_SUBMISSION_TIME = 3000; // Minimum 3 seconds to fill form

const Contact = () => {
  const { setCurrentView, showNotification } = useApp();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [honeypot, setHoneypot] = useState('');
  const [formLoadTime, setFormLoadTime] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setFormLoadTime(Date.now());
  }, []);

  const checkRateLimit = () => {
    try {
      const stored = localStorage.getItem(RATE_LIMIT_KEY);
      if (!stored) return true;

      const submissions = JSON.parse(stored);
      const now = Date.now();
      const recentSubmissions = submissions.filter(
        time => now - time < RATE_LIMIT_WINDOW
      );

      return recentSubmissions.length < RATE_LIMIT_MAX;
    } catch {
      return true;
    }
  };

  const recordSubmission = () => {
    try {
      const stored = localStorage.getItem(RATE_LIMIT_KEY);
      const submissions = stored ? JSON.parse(stored) : [];
      const now = Date.now();

      const recentSubmissions = submissions.filter(
        time => now - time < RATE_LIMIT_WINDOW
      );
      recentSubmissions.push(now);

      localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(recentSubmissions));
    } catch {
      // Ignore storage errors
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    // Check honeypot (should be empty)
    if (honeypot) {
      console.warn('Honeypot triggered');
      return { valid: false, silent: true };
    }

    // Check submission time (too fast = bot)
    const timeTaken = Date.now() - formLoadTime;
    if (timeTaken < MIN_SUBMISSION_TIME) {
      console.warn('Form submitted too quickly');
      return { valid: false, silent: true };
    }

    // Check rate limiting
    if (!checkRateLimit()) {
      return {
        valid: false,
        error: 'Too many submissions. Please try again later.'
      };
    }

    // Validate required fields
    if (!formData.name.trim()) {
      return { valid: false, error: 'Please enter your name.' };
    }

    if (!formData.email.trim()) {
      return { valid: false, error: 'Please enter your email address.' };
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return { valid: false, error: 'Please enter a valid email address.' };
    }

    if (!formData.message.trim()) {
      return { valid: false, error: 'Please enter a message.' };
    }

    if (formData.message.trim().length < 10) {
      return { valid: false, error: 'Message is too short. Please provide more details.' };
    }

    return { valid: true };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validation = validateForm();

    if (!validation.valid) {
      if (validation.silent) {
        // Fake success for bot submissions
        setSubmitted(true);
        return;
      }
      showNotification(validation.error, 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit to Netlify Forms
      const form = e.target;
      const formDataObj = new FormData(form);

      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataObj).toString()
      });

      if (response.ok) {
        recordSubmission();
        setSubmitted(true);
        showNotification('Message sent successfully!', 'success');
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      showNotification('Failed to send message. Please try again.', 'error');
    }

    setIsSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="page-container">
        <div className="page-header">
          <button className="btn btn-ghost" onClick={() => setCurrentView('upload')}>
            ← Back to Home
          </button>
          <h1>Contact Us</h1>
        </div>

        <div className="page-content">
          <div className="success-message">
            <div className="success-icon">✓</div>
            <h2>Thank You!</h2>
            <p>Your message has been sent successfully. We'll get back to you as soon as possible.</p>
            <button className="btn btn-primary" onClick={() => setCurrentView('upload')}>
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <button className="btn btn-ghost" onClick={() => setCurrentView('upload')}>
          ← Back to Home
        </button>
        <h1>Contact Us</h1>
      </div>

      <div className="page-content">
        <div className="contact-intro">
          <p>
            Have a question, feedback, or need help? Fill out the form below and we'll
            get back to you as soon as possible.
          </p>
        </div>

        <form
          className="contact-form"
          name="contact"
          method="POST"
          data-netlify="true"
          data-netlify-honeypot="bot-field"
          onSubmit={handleSubmit}
          netlify
        >
          {/* Netlify form name */}
          <input type="hidden" name="form-name" value="contact" />

          {/* Honeypot */}
          <div className="form-field-honeypot">
            <label>
              Don't fill this out if you're human:
              <input
                type="text"
                name="bot-field"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </label>
          </div>

          {/* Timestamp field for bot detection */}
          <input
            type="hidden"
            name="form-load-time"
            value={formLoadTime || ''}
          />

          <div className="form-field">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your name"
              required
              maxLength={100}
              autoComplete="name"
            />
          </div>

          <div className="form-field">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your@email.com"
              required
              maxLength={254}
              autoComplete="email"
            />
          </div>

          <div className="form-field">
            <label htmlFor="subject">Subject</label>
            <select
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
            >
              <option value="">Select a topic...</option>
              <option value="general">General Inquiry</option>
              <option value="support">Technical Support</option>
              <option value="feedback">Feedback</option>
              <option value="bug">Report a Bug</option>
              <option value="feature">Feature Request</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="form-field">
            <label htmlFor="message">Message *</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="How can we help you?"
              required
              rows={6}
              maxLength={5000}
            />
            <span className="char-count">{formData.message.length}/5000</span>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  Sending...
                </>
              ) : (
                'Send Message'
              )}
            </button>
          </div>
        </form>

        <div className="contact-alt">
          <p>
            You can also check our <button className="link-button" onClick={() => setCurrentView('faq')}>FAQ</button> for
            answers to common questions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Contact;
